import React, { Fragment, useContext } from 'react';
import styled from 'styled-components/macro';
import times from 'lodash.times';
import { EntityContext } from 'context/entityContext';

import GenericComponent from 'components/utility/GenericComponent/GenericComponent';
import AnimatedLoadingWrapper from 'components/base/AnimatedLoadingWrapper';
import KeyValueLoader from 'components/utility/ContentLoader/KeyValueLoader/KeyValueLoader';
import ContentLoader from 'components/utility/ContentLoader/ContentLoader';
import { ComponentStatisticsLoaderWrapper } from 'components/ComponentStatistics/ComponentStatistics';

interface IObject {
  [key: string]: any;
}

const Relative = styled('div')<IObject>`
  position: relative;
  min-height: ${({ minHeight }): string => `${minHeight}px`};
  margin-bottom: ${({ theme }): string => theme.spacing.xlarge};
`;

const ComponentStatisticsLoader = ({
  count,
}: {
  count: number;
}): JSX.Element => (
  <ComponentStatisticsLoaderWrapper>
    {times(count, (idx: number) => (
      <Fragment key={`stats-loader-${idx}`}>
        <KeyValueLoader />
        <KeyValueLoader />
      </Fragment>
    ))}
  </ComponentStatisticsLoaderWrapper>
);

const StyledContentLoader = styled(ContentLoader)<IObject>`
  position: absolute;
  left: 0;
  top: ${({ index, margin, size, height, count }): string => {
    const rowHeight = size / count;
    return `${margin.top + rowHeight * index + rowHeight / 2 - height / 2}px`;
  }};
`;

interface IMargin {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

const BarChartLoader = ({
  margin,
  innerHeight,
  barCount,
}: {
  margin: IMargin;
  innerHeight: number;
  barCount: number;
}): JSX.Element => (
  <>
    {Array(barCount)
      .fill(0)
      .map((_: any, index: number) => (
        <StyledContentLoader
          width={110}
          height={16}
          index={index}
          margin={margin}
          size={innerHeight}
          key={`performance-breakdown-loader-${index}`}
          count={barCount}
        >
          <rect width="110" height="16" x="0" y="0" />
        </StyledContentLoader>
      ))}
  </>
);

interface ISkeletonLoader {
  title?: string;
  barCount: number;
  isWide?: boolean;
}

const SkeletonLoader: React.FC<ISkeletonLoader> = (props) => {
  const { entityIds } = useContext(EntityContext);
  const { title = 'RTD Performance Breakdown', barCount, isWide } = props;

  const margin = {
    top: 20,
    right: 1,
    bottom: 40,
    left: 140,
  };

  const height = (2 + (entityIds.length - 1)) * (barCount * 20);

  return (
    <GenericComponent title={title} loading isWide={isWide} isNarrowScreen>
      <AnimatedLoadingWrapper loading>
        <ComponentStatisticsLoader count={entityIds.length} />
        <Relative minHeight={height}>
          <BarChartLoader
            margin={margin}
            innerHeight={height}
            barCount={barCount}
          />
        </Relative>
      </AnimatedLoadingWrapper>
    </GenericComponent>
  );
};

export default SkeletonLoader;
